// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, Save, X, CalendarClock, Copy, Check, RotateCcw, Gift } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  CONSTRAINT_TYPE_LABELS,
  SEVERITY_LABELS,
  humanizeConstraint,
} from "@/lib/constraints/humanize";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { getSessionUser } from '@/lib/authSession';

const CONSTRAINT_REVIEW_STATUSES = [
  { value: "pending_review", label: "Pending review" },
  { value: "approved", label: "Accept" },
  { value: "denied", label: "Decline" },
  { value: "partial", label: "Partial" },
];

function SchedulingRequests({
  teamId,
  eventId,
}: {
  teamId: string | null;
  eventId: string | null;
}) {
  const [edits, setEdits] = useState<Record<string, { status: string; response_notes: string }>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const { data: constraints, isLoading, refetch } = useQuery({
    queryKey: ["regdash-scheduling-constraints", teamId, eventId],
    enabled: !!teamId && !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("schedule_constraints")
        .select("*")
        .eq("team_id", teamId)
        .eq("event_id", eventId)
        .is("deleted_at", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as any[];
    },
  });

  // Look up related team names for coach_conflict constraints
  const relatedTeamIds = useMemo(() => {
    const ids = new Set<string>();
    (constraints ?? []).forEach((c) => {
      const rid = c.parameters?.related_team_id;
      if (rid) ids.add(rid);
    });
    return Array.from(ids);
  }, [constraints]);

  const { data: teamNames } = useQuery({
    queryKey: ["regdash-related-team-names", relatedTeamIds],
    enabled: relatedTeamIds.length > 0,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("teams")
        .select("id, display_name, name")
        .in("id", relatedTeamIds);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data ?? []).forEach((t: any) => {
        map[t.id] = t.display_name || t.name;
      });
      return map;
    },
  });

  const opponentNameLookup = (id: string) => teamNames?.[id];

  const getEdit = (c: any) =>
    edits[c.id] ?? { status: c.status ?? "pending_review", response_notes: c.response_notes ?? "" };

  const setEdit = (id: string, patch: Partial<{ status: string; response_notes: string }>) =>
    setEdits((prev) => ({
      ...prev,
      [id]: { ...getEditFor(prev, id), ...patch },
    }));

  const getEditFor = (
    prev: Record<string, { status: string; response_notes: string }>,
    id: string,
  ) => {
    if (prev[id]) return prev[id];
    const c = (constraints ?? []).find((x) => x.id === id);
    return { status: c?.status ?? "pending_review", response_notes: c?.response_notes ?? "" };
  };

  const handleSaveConstraint = async (c: any) => {
    const edit = getEdit(c);
    setSavingId(c.id);
    try {
      const { data: userData } = await getSessionUser();
      const uid = userData?.user?.id ?? null;
      const { error } = await (supabase as any)
        .from("schedule_constraints")
        .update({
          status: edit.status,
          response_notes: edit.response_notes || null,
          reviewed_by: uid,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", c.id);
      if (error) throw error;
      toast.success("Scheduling request updated");
      setEdits((prev) => {
        const next = { ...prev };
        delete next[c.id];
        return next;
      });
      refetch();
    } catch (e: any) {
      toast.error(e.message || "Failed to update request");
    } finally {
      setSavingId(null);
    }
  };

  if (!teamId || !eventId) return null;

  return (
    <section>
      <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
        <CalendarClock className="h-4 w-4" /> Scheduling Requests
      </h4>
      {isLoading ? (
        <div className="flex items-center text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading requests...
        </div>
      ) : !constraints || constraints.length === 0 ? (
        <p className="text-sm text-muted-foreground">No scheduling requests for this team.</p>
      ) : (
        <div className="space-y-4">
          {constraints.map((c) => {
            const edit = getEdit(c);
            return (
              <div key={c.id} className="rounded-md border p-3 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-sm font-semibold">
                    {CONSTRAINT_TYPE_LABELS[c.constraint_type] || c.constraint_type}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {SEVERITY_LABELS[c.severity] || c.severity}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">{c.status}</Badge>
                  </div>
                </div>
                <p className="text-sm">{humanizeConstraint(c, { opponentNameLookup })}</p>
                <div className="text-xs text-muted-foreground">
                  Fee: {c.fee_charged != null ? `$${Number(c.fee_charged).toFixed(2)}` : "—"}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Field label="Review status">
                    <Select value={edit.status} onValueChange={(v) => setEdit(c.id, { status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CONSTRAINT_REVIEW_STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Response notes">
                      <Textarea
                        value={edit.response_notes}
                        onChange={(e) => setEdit(c.id, { response_notes: e.target.value })}
                        className="min-h-[60px]"
                      />
                    </Field>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => handleSaveConstraint(c)} disabled={savingId === c.id}>
                    {savingId === c.id ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Save review
                  </Button>
                  {c.reviewed_at && (
                    <span className="text-xs text-muted-foreground">
                      Last reviewed {fmtDateTime(c.reviewed_at)}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

const REGISTRATION_STATUSES = [
  "draft",
  "registered",
  "confirmed",
  "waitlisted",
  "cancelled",
  "withdrawn",
];
const PAYMENT_STATUSES = ["unpaid", "partial", "paid", "refunded"];
const PAYMENT_METHODS = ["online", "check", "cash", "venmo", "wire", "invoice", "other"];
const SCHEDULING_TIERS = ["standard", "priority", "club_priority", "showcase"];

// Statuses where money has moved — editable amount fields are locked.
const MONEY_MOVED_STATUSES = ["paid", "partially_refunded", "refunded"];

const fmtDateTime = (s: string | null | undefined) =>
  s ? format(new Date(s), "MMM d, yyyy h:mm a") : "—";

const fmtMoney = (v: any) =>
  v == null || v === "" ? "—" : `$${Number(v).toFixed(2)}`;

function CopyButton({ value }: { value: string | null | undefined }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-6 w-6 shrink-0"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          toast.error("Could not copy");
        }
      }}
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

// Convert a timestamptz string to a value usable in <input type="date">
const toDateInput = (s: string | null | undefined) =>
  s ? format(new Date(s), "yyyy-MM-dd") : "";

// Convert a date-input value back to an ISO timestamp (midnight local)
const fromDateInput = (s: string) => (s ? new Date(`${s}T00:00:00`).toISOString() : null);

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function ReadOnly({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-sm font-mono break-all">{value ?? "—"}</div>
    </div>
  );
}

// Locked value styled like a disabled input, for use inside <Field>.
function ReadOnlyValue({ value }: { value: React.ReactNode }) {
  return (
    <div className="flex h-10 items-center rounded-md border border-input bg-muted/50 px-3 text-sm">
      {value ?? "—"}
    </div>
  );
}


export default function InlineRegistrationEditor({
  registrationId,
  onClose,
  onSaved,
}: {
  registrationId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [compOpen, setCompOpen] = useState(false);
  const [compReason, setCompReason] = useState("");
  const [processing, setProcessing] = useState(false);

  const { data: reg, isLoading } = useQuery({
    queryKey: ["regdash-reg-detail", registrationId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("event_registrations_v2")
        .select("*")
        .eq("id", registrationId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (reg) setForm({ ...reg });
  }, [reg]);

  const set = (key: string, value: any) => setForm((f: any) => ({ ...f, [key]: value }));

  const checkedIn = !!form?.checked_in_at;

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const num = (v: any) => (v === "" || v == null ? null : Number(v));
      const payload = {
        registration_status: form.registration_status,
        payment_status: form.payment_status,
        total_amount_due: num(form.total_amount_due),
        total_amount_paid: num(form.total_amount_paid),
        payment_received_method: form.payment_received_method || null,
        payment_received_at: form.payment_received_at || null,
        payment_received_reference: form.payment_received_reference || null,
        payment_due_at: form.payment_due_at || null,
        payment_method_chosen: form.payment_method_chosen || null,
        division: form.division || null,
        age_group: form.age_group || null,
        pool: form.pool || null,
        scheduling_tier: form.scheduling_tier || null,
        scheduling_tier_fee: num(form.scheduling_tier_fee),
        roster_locked: !!form.roster_locked,
        checked_in_at: form.checked_in_at || null,
        primary_billing_email: form.primary_billing_email || null,
        coach_phone: form.coach_phone || null,
        admin_notes: form.admin_notes || null,
        registrant_notes: form.registrant_notes || null,
        special_requests: form.special_requests || null,
        special_requests_fee: num(form.special_requests_fee),
      };
      const { error } = await (supabase as any)
        .from("event_registrations_v2")
        .update(payload)
        .eq("id", registrationId);
      if (error) throw error;
      toast.success("Registration updated");
      onSaved();
    } catch (e: any) {
      toast.error(e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const paymentStatus = form?.payment_status ?? "";
  const moneyMoved = MONEY_MOVED_STATUSES.includes(paymentStatus);
  const paid = Number(form?.total_amount_paid || 0);
  const refunded = Number(form?.total_amount_refunded || 0);
  const refundable = Math.max(0, paid - refunded);
  const canRefund = refundable > 0;
  const canComp = paymentStatus !== "paid" && paid === 0 && paymentStatus !== "comped";
  const isComped = paymentStatus === "comped";

  const refetchAll = async () => {
    const { data } = await (supabase as any)
      .from("event_registrations_v2")
      .select("*")
      .eq("id", registrationId)
      .single();
    if (data) setForm({ ...data });
    onSaved();
  };

  const openRefund = () => {
    setRefundAmount(refundable.toFixed(2));
    setRefundOpen(true);
  };

  const handleRefund = async () => {
    const amount = Number(refundAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a refund amount");
      return;
    }
    setProcessing(true);
    try {
      const { data, error } = await invokeEdgeFunction("refund-registration", {
        body: { registration_id: registrationId, amount },
      });
      if (error) {
        // Try to surface the function's JSON error message
        let msg = error.message;
        try {
          const ctx = await (error as any).context?.json?.();
          if (ctx?.error) msg = ctx.error;
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      toast.success("Refund processed");
      setRefundOpen(false);
      await refetchAll();
    } catch (e: any) {
      toast.error(e.message || "Refund failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleComp = async () => {
    setProcessing(true);
    try {
      const { data, error } = await invokeEdgeFunction("comp-registration", {
        body: { registration_id: registrationId, reason: compReason || null },
      });
      if (error) {
        let msg = error.message;
        try {
          const ctx = await (error as any).context?.json?.();
          if (ctx?.error) msg = ctx.error;
        } catch { /* ignore */ }
        throw new Error(msg);
      }
      if (data?.error) throw new Error(data.error);
      toast.success("Registration comped");
      setCompOpen(false);
      setCompReason("");
      await refetchAll();
    } catch (e: any) {
      toast.error(e.message || "Comp failed");
    } finally {
      setProcessing(false);
    }
  };



  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading registration...
      </div>
    );
  }

  return (
    <div className="space-y-6 py-2">
      {/* Status */}
      <section>
        <h4 className="text-sm font-semibold mb-3">Status</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Registration status">
            <Select
              value={form.registration_status ?? ""}
              onValueChange={(v) => set("registration_status", v)}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {REGISTRATION_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Payment status">
            {moneyMoved || isComped ? (
              <div className="flex h-10 items-center">
                <Badge variant="secondary" className="capitalize">
                  {paymentStatus.replace(/_/g, " ")}
                </Badge>
                <span className="ml-2 text-xs text-muted-foreground">
                  (changes via actions below)
                </span>
              </div>
            ) : (
              <Select
                value={form.payment_status ?? ""}
                onValueChange={(v) => set("payment_status", v)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        </div>
      </section>

      {/* Payment */}
      <section>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <h4 className="text-sm font-semibold">Payment</h4>
          <div className="flex items-center gap-2">
            {canRefund && (
              <Button size="sm" variant="outline" onClick={openRefund}>
                <RotateCcw className="h-4 w-4 mr-2" /> Refund
              </Button>
            )}
            {canComp && (
              <Button size="sm" variant="outline" onClick={() => setCompOpen(true)}>
                <Gift className="h-4 w-4 mr-2" /> Comp / Waive
              </Button>
            )}
          </div>
        </div>

        {moneyMoved && (
          <p className="text-xs text-muted-foreground mb-3">
            Payment has been collected — amount fields are locked. Use the actions above to refund.
          </p>
        )}

        {/* Editable amount fields (locked once money has moved) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Total amount due">
            {moneyMoved ? (
              <ReadOnlyValue value={fmtMoney(form.total_amount_due)} />
            ) : (
              <Input type="number" step="0.01" value={form.total_amount_due ?? ""}
                onChange={(e) => set("total_amount_due", e.target.value)} />
            )}
          </Field>
          <Field label="Payment method chosen">
            {moneyMoved ? (
              <ReadOnlyValue value={form.payment_method_chosen} />
            ) : (
              <Select value={form.payment_method_chosen ?? ""}
                onValueChange={(v) => set("payment_method_chosen", v)}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
          <Field label="Payment due at">
            {moneyMoved ? (
              <ReadOnlyValue value={form.payment_due_at ? fmtDateTime(form.payment_due_at) : null} />
            ) : (
              <Input type="date" value={toDateInput(form.payment_due_at)}
                onChange={(e) => set("payment_due_at", fromDateInput(e.target.value))} />
            )}
          </Field>
        </div>

        {/* Always read-only payment facts */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ReadOnly label="Total amount paid" value={fmtMoney(form.total_amount_paid)} />
          <ReadOnly label="Payment received method" value={form.payment_received_method} />
          <ReadOnly label="Payment received at" value={fmtDateTime(form.payment_received_at)} />
          <ReadOnly label="Payment received reference" value={form.payment_received_reference} />
          {refunded > 0 && (
            <ReadOnly label="Total amount refunded" value={fmtMoney(form.total_amount_refunded)} />
          )}
          {isComped && (
            <ReadOnly label="Comp reason" value={form.comp_reason || "(no reason given)"} />
          )}
        </div>

        {/* Payment details (Stripe identifiers) */}
        <div className="mt-4 rounded-md border bg-muted/30 p-3 space-y-3">
          <div className="text-xs font-semibold text-muted-foreground">Payment details</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Stripe session ID</div>
              <div className="flex items-center gap-1">
                <span className="text-sm font-mono break-all">{form.stripe_session_id || "—"}</span>
                <CopyButton value={form.stripe_session_id} />
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">Stripe payment intent ID</div>
              <div className="flex items-center gap-1">
                <span className="text-sm font-mono break-all">{form.stripe_payment_intent_id || "—"}</span>
                <CopyButton value={form.stripe_payment_intent_id} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Refund dialog */}
      <Dialog open={refundOpen} onOpenChange={(o) => !processing && setRefundOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refund registration</DialogTitle>
            <DialogDescription>
              Refundable balance: {fmtMoney(refundable)}. Lower the amount for a partial refund.
            </DialogDescription>
          </DialogHeader>
          <Field label="Refund amount">
            <Input type="number" step="0.01" min="0" max={refundable}
              value={refundAmount} onChange={(e) => setRefundAmount(e.target.value)} />
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRefundOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleRefund} disabled={processing}>
              {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Refund {fmtMoney(refundAmount)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comp dialog */}
      <Dialog open={compOpen} onOpenChange={(o) => !processing && setCompOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Comp / waive registration</DialogTitle>
            <DialogDescription>
              The amount due stays on record as forgiven (not zeroed). Add an optional reason.
            </DialogDescription>
          </DialogHeader>
          <Field label="Reason (optional)">
            <Textarea value={compReason} onChange={(e) => setCompReason(e.target.value)}
              placeholder="e.g. Sponsor invite, hardship waiver" />
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCompOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleComp} disabled={processing}>
              {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Gift className="h-4 w-4 mr-2" />}
              Comp registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Placement */}
      <section>
        <h4 className="text-sm font-semibold mb-3">Placement</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Division">
            <Input value={form.division ?? ""} onChange={(e) => set("division", e.target.value)} />
          </Field>
          <Field label="Age group">
            <Input value={form.age_group ?? ""} onChange={(e) => set("age_group", e.target.value)} />
          </Field>
          <Field label="Pool">
            <Input value={form.pool ?? ""} onChange={(e) => set("pool", e.target.value)} />
          </Field>
        </div>
      </section>

      {/* Scheduling */}
      <section>
        <h4 className="text-sm font-semibold mb-3">Scheduling</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Scheduling tier">
            <Select value={form.scheduling_tier ?? ""}
              onValueChange={(v) => set("scheduling_tier", v)}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                {SCHEDULING_TIERS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Scheduling tier fee">
            <Input type="number" step="0.01" value={form.scheduling_tier_fee ?? ""}
              onChange={(e) => set("scheduling_tier_fee", e.target.value)} />
          </Field>
        </div>
      </section>

      {/* Roster / check-in */}
      <section>
        <h4 className="text-sm font-semibold mb-3">Roster &amp; Check-in</h4>
        <div className="flex flex-wrap gap-8">
          <div className="flex items-center gap-3">
            <Switch checked={!!form.roster_locked}
              onCheckedChange={(v) => set("roster_locked", v)} />
            <Label className="text-sm">Roster locked</Label>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={checkedIn}
              onCheckedChange={(v) => set("checked_in_at", v ? new Date().toISOString() : null)} />
            <Label className="text-sm">
              Checked in{checkedIn ? ` (${fmtDateTime(form.checked_in_at)})` : ""}
            </Label>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section>
        <h4 className="text-sm font-semibold mb-3">Contact</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Primary billing email">
            <Input type="email" value={form.primary_billing_email ?? ""}
              onChange={(e) => set("primary_billing_email", e.target.value)} />
          </Field>
          <Field label="Coach phone">
            <Input value={form.coach_phone ?? ""}
              onChange={(e) => set("coach_phone", e.target.value)} />
          </Field>
        </div>
      </section>

      {/* Notes / requests */}
      <section>
        <h4 className="text-sm font-semibold mb-3">Notes &amp; Requests</h4>
        <div className="grid grid-cols-1 gap-4">
          <Field label="Admin notes">
            <Textarea value={form.admin_notes ?? ""}
              onChange={(e) => set("admin_notes", e.target.value)} />
          </Field>
          <Field label="Registrant notes">
            <Textarea value={form.registrant_notes ?? ""}
              onChange={(e) => set("registrant_notes", e.target.value)} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Field label="Special requests">
                <Textarea value={form.special_requests ?? ""}
                  onChange={(e) => set("special_requests", e.target.value)} />
              </Field>
            </div>
            <Field label="Special requests fee">
              <Input type="number" step="0.01" value={form.special_requests_fee ?? ""}
                onChange={(e) => set("special_requests_fee", e.target.value)} />
            </Field>
          </div>
        </div>
      </section>

      {/* Scheduling Requests (schedule_constraints) */}
      <SchedulingRequests teamId={form.team_id} eventId={form.event_id} />



      {/* System / read-only */}
      <section>
        <h4 className="text-sm font-semibold mb-3">System (read-only)</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ReadOnly label="Registration ID" value={form.id} />
          <ReadOnly label="Event ID" value={form.event_id} />
          <ReadOnly label="Team ID" value={form.team_id} />
          <ReadOnly label="Stripe session ID" value={form.stripe_session_id} />
          <ReadOnly label="Stripe payment intent ID" value={form.stripe_payment_intent_id} />
          <ReadOnly label="Created at" value={fmtDateTime(form.created_at)} />
          <ReadOnly label="Registered by" value={form.registered_by_user_id} />
          <ReadOnly label="Payment marked received by" value={form.payment_marked_received_by} />
          <ReadOnly label="Roster locked by" value={form.roster_locked_by} />
          <ReadOnly label="Checked in by" value={form.checked_in_by} />
        </div>
      </section>

      <div className="flex items-center gap-2 pt-2 border-t">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save
        </Button>
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          <X className="h-4 w-4 mr-2" /> Cancel
        </Button>
      </div>
    </div>
  );
}
